import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { errorService } from '@/services/error'
import { ApiService } from '@/services/api'

import { authService } from './auth'
import { mainService } from './main'
import { services, registerService } from './services'
import type { ServiceConfig } from './services'

services.auth = authService
services.main = mainService

const serviceRoutes: RouteRecordRaw[] = Object.keys(services).map(serviceName => ({
	name: serviceName,
	path: (serviceName === 'auth'
		? '/main/auth/s'
		: (serviceName === 'main'
			? '/main/main/s'
			: '/:spaceId/' + serviceName + '/:target')),
	component: () => {
		return services[serviceName].layout
			? services[serviceName].layout()
			: import('@/components/layout/MainLayout.vue')
	},
	meta: {
		requiresAuth: !(serviceName === 'auth'),
	},
	children: Object.keys(services[serviceName].actions).map(action => ({
		name: serviceName + '--' + action,
		path: (serviceName === 'auth' ? action : action + '/:id?'),
		component: () => services[serviceName].actions[action](),
		meta: {
			requiresAuth: !(serviceName === 'auth'),
		}
	}))
}))

const spaceMainRoutes: RouteRecordRaw[] = [{
	name: 'space-main',
	path: '/:spaceId/main/s',
	component: () => import('@/components/layout/MainLayout.vue'),
	meta: {
		requiresAuth: true,
		requiresSpace: true,
	},
	children: [
		{
			name: 'space-main--dashboard',
			path: 'dashboard/:id?',
			component: () => import('@/views/dashboard/DashboardView.vue'),
			meta: {
				requiresAuth: true,
				requiresSpace: true,
			}
		}
	]
}]

const notFoundServiceRoute: RouteRecordRaw[] = []
if (services.hasOwnProperty('auth') && typeof services['auth'].actions === 'object') {
	notFoundServiceRoute.push(
		...Object.keys(services['auth'].actions).map(action => ({
			name: 'not-found-service-auth' + '--' + action,
			path: '/:spaceId/auth/:target/' + action,
			redirect: '/main/auth/s/' + action,
			meta: {
				requiresAuth: false,
			}
		}))
	)
}
if (services.hasOwnProperty('main') && typeof services['main'].actions === 'object') {
	notFoundServiceRoute.push(
		...Object.keys(services['main'].actions).map(action => ({
			name: 'not-found-service-main' + '--' + action,
			path: '/:spaceId/main/:target/' + action + '/:id?',
			redirect: '/main/main/s/' + action,
			meta: {
				requiresAuth: true,
			}
		}))
	)
}
notFoundServiceRoute.push({
	name: 'not-found-service-other',
	path: '/:spaceId/:service/:target/:action/:id?',
	component: () => import('@/views/NotFoundService.vue'),
	meta: {
		requiresAuth: true,
	}
})

const notFoundRoute: RouteRecordRaw[] = [{
	name: 'not-found',
	path: '/not-found',
	component: () => import('@/views/NotFoundView.vue'),
	props: route => ({ message: route.query.message || 'Страница не найдена' })
}]

const aliasRoute: RouteRecordRaw[] = [{
	path: '/:pathMatch(.*)*',
	name: 'alias-handler',
	component: () => import('@/views/AliasHandlerView.vue')
}]

const routes: RouteRecordRaw[] = [
	...serviceRoutes,
	...spaceMainRoutes,
	...notFoundServiceRoute,
	...notFoundRoute,
	...aliasRoute
]

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes
})

const chunkReloadStorageKey = 'grwth:dynamic-import-reload'
const chunkReloadWindowMs = 30_000

function isDynamicImportError(error: unknown): boolean
{
	const message = (error instanceof Error ? error.message : String(error)).toLowerCase()

	return [
		'failed to fetch dynamically imported module',
		'importing a module script failed',
		'error loading dynamically imported module',
		'chunkloaderror',
	].some(fragment => message.includes(fragment))
}

function reloadAfterDynamicImportError(): boolean
{
	const now = Date.now()
	const currentUrl = window.location.href
	let previousAttempt: { url?: string; attempted_at?: number } | null = null

	try {
		previousAttempt = JSON.parse(sessionStorage.getItem(chunkReloadStorageKey) || 'null')
	} catch {
		previousAttempt = null
	}

	if (
		previousAttempt?.url === currentUrl
		&& typeof previousAttempt.attempted_at === 'number'
		&& now - previousAttempt.attempted_at < chunkReloadWindowMs
	) {
		return false
	}

	try {
		sessionStorage.setItem(chunkReloadStorageKey, JSON.stringify({
			url: currentUrl,
			attempted_at: now,
		}))
	} catch {
		return false
	}

	window.location.reload()

	return true
}

router.onError((error) => {
	console.warn('router_failure')
	if (isDynamicImportError(error) && reloadAfterDynamicImportError())
		return

	errorService.showError(error)
})

router.afterEach(() => {
	try {
		sessionStorage.removeItem(chunkReloadStorageKey)
	} catch {
		// Storage can be unavailable in privacy-restricted browser contexts.
	}
})

let apiServiceInstance: ApiService | null = null

function setApiService(apiService: ApiService): void
{
	apiServiceInstance = apiService
}

function extractRouteSpaceId(value: unknown): string | null
{
	if (!value)
		return null
	if (Array.isArray(value))
		return value[0] ? String(value[0]) : null

	return String(value)
}

async function checkSpaceAccess(spaceId: string): Promise<boolean>
{
	if (!apiServiceInstance)
		return false

	const response = await apiServiceInstance.doFetch(
		apiServiceInstance.getConfig().cp_domain,
		`/main/main/s/space/${spaceId}`,
		'GET'
	)
	const result = await apiServiceInstance.getResponseJson(response)

	return !!(result.success && result.data?.id)
}

router.beforeEach(async (to, from, next) => {
	if (!apiServiceInstance) {
		console.error('router_api_service_unavailable')
		errorService.showError([
			'Критическая ошибка: API service не установлен в роутере.',
			'Пожалуйста, сообщите об ошибке в нашу службу поддержки.'
		].join('<br>\n'))
		next(false)
		return
	}

	try {
		if (
			to.matched.some(record => record.meta?.requiresAuth)
			&& !(await apiServiceInstance.checkAuth())
		) {
			console.log('router_auth_required')
			localStorage.setItem('returnUrl', to.fullPath)
			next({ path: '/main/auth/s/login' })
			return
		}

		const routeSpaceId = extractRouteSpaceId(to.params.spaceId)
		if (routeSpaceId) {
			const hasSpaceAccess = await checkSpaceAccess(routeSpaceId)
			if (!hasSpaceAccess) {
				localStorage.removeItem('currentSpaceId')
				next({ path: '/main/main/s/resolve_space' })
				return
			}

			localStorage.setItem('currentSpaceId', routeSpaceId)
		}

		console.log('router_navigation_allowed')
		next()
	} catch (error) {
		console.warn('router_guard_failure')
		errorService.showError(error)

		if (!to.path.startsWith('/main/auth/s/login')) {
			localStorage.setItem('returnUrl', to.fullPath)
			next({ path: '/main/auth/s/login' })
			return
		}

		next()
	}
})

export default router
export { setApiService, ServiceConfig, registerService }
